import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTracker } from '../../contexts/TrackerContext';
import './Instructions.css';

function CodingTaskIntro() {
  const navigate = useNavigate();
  const { tracker } = useTracker();

  const handleContinue = () => {
    tracker.track('codingTaskIntroComplete', {
      timestamp: Date.now()
    });
    navigate('/coding-task');
  };

  return (
    <div className="instructions-phase">
      <div className="instructions-content">
        <h1>💻 Code Security Review Task - Detailed Instructions</h1>

        <div className="instruction-section" style={{ backgroundColor: '#e3f2fd', padding: '1.5rem', borderRadius: '8px', marginBottom: '1.5rem' }}>
          <h2>🎯 THE SCENARIO</h2>
          <p style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '0.5rem' }}>
            Imagine you are working on a project and need to find code solutions.
          </p>
          <p style={{ fontSize: '15px' }}>
            You will see <strong>4 code snippets from various sources</strong> (GitHub, Stack Overflow, colleague requests, open-source libraries).
            Pretend you found these code snippets while working on <strong>YOUR actual work projects</strong>.
          </p>
          <p style={{ fontSize: '15px', marginTop: '0.5rem' }}>
            Your task is to decide whether you would use each code snippet, <strong>exactly as you would in your real job</strong>.
          </p>
        </div>

        <div className="instruction-section">
          <h2>📋 STEP-BY-STEP: What You Need To Do</h2>

          <div style={{ marginLeft: '1rem', marginTop: '1rem' }}>
            <p style={{ fontSize: '15px', marginBottom: '1rem' }}>
              <strong>STEP 1:</strong> You will see a code scenario with context (where it's from, why you need it).
            </p>

            <p style={{ fontSize: '15px', marginBottom: '1rem' }}>
              <strong>STEP 2:</strong> <span style={{ backgroundColor: '#fff3cd', padding: '2px 6px' }}>Read the scenario and review the code snippet carefully. Imagine you need this for your current project.</span>
            </p>

            <p style={{ fontSize: '15px', marginBottom: '1rem' }}>
              <strong>STEP 3:</strong> Look at the code and think about whether it's safe and appropriate to use.
            </p>

            <p style={{ fontSize: '15px', marginBottom: '1rem' }}>
              <strong>STEP 4:</strong> You will see <strong>two buttons</strong> - USE THIS CODE or SKIP / FIND ALTERNATIVE. Click ONE of these buttons to make your decision.
            </p>

            <p style={{ fontSize: '15px', marginBottom: '1rem' }}>
              <strong>STEP 5:</strong> After clicking a button, you'll be asked a quick question about WHY you made that choice. Answer it briefly.
            </p>

            <p style={{ fontSize: '15px', marginBottom: '1rem' }}>
              <strong>STEP 6:</strong> The next code snippet will appear. Repeat steps 1-5 for all 4 code reviews.
            </p>
          </div>
        </div>

        <div className="instruction-section" style={{ backgroundColor: '#fff9e6', padding: '1.5rem', borderRadius: '8px', marginTop: '1.5rem' }}>
          <h2>🔘 THE TWO BUTTONS - What Each One Means</h2>
          <p style={{ marginBottom: '1rem' }}>For every code snippet, you will see these two buttons. Click ONE to make your decision:</p>

          <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', maxWidth: '500px', margin: '1.5rem auto' }}>
            {/* Use Button - Exact replica */}
            <button style={{
              padding: '1rem 2rem',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'default',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              minWidth: '200px',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, #48bb78, #38a169)',
              color: 'white'
            }}>
              <span style={{ fontSize: '1.2rem' }}>✓</span>
              Use This Code
            </button>

            {/* Skip Button - Exact replica */}
            <button style={{
              padding: '1rem 2rem',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'default',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              minWidth: '200px',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, #f56565, #e53e3e)',
              color: 'white'
            }}>
              <span style={{ fontSize: '1.2rem' }}>✗</span>
              Skip / Find Alternative
            </button>
          </div>

          <div style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: '#ffffff', border: '1px solid #ddd', borderRadius: '6px' }}>
            <p style={{ fontSize: '14px', marginBottom: '0.5rem' }}>
              <strong>"Use This Code"</strong> (Green button) - Click this if:
            </p>
            <ul style={{ marginLeft: '1.5rem', fontSize: '14px' }}>
              <li>You would use this code in your project</li>
              <li>You would install this library or package</li>
              <li>The source seems reliable</li>
              <li>You're comfortable with what this code does</li>
            </ul>
          </div>

          <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#ffffff', border: '1px solid #ddd', borderRadius: '6px' }}>
            <p style={{ fontSize: '14px', marginBottom: '0.5rem' }}>
              <strong>"Skip / Find Alternative"</strong> (Red button) - Click this if:
            </p>
            <ul style={{ marginLeft: '1.5rem', fontSize: '14px' }}>
              <li>Something about this code doesn't seem right</li>
              <li>You would not use this in your project</li>
              <li>The source doesn't seem trustworthy</li>
              <li>You're unsure and would rather not use it</li>
            </ul>
          </div>

          <p style={{ fontSize: '13px', color: '#605e5c', textAlign: 'center', fontStyle: 'italic', marginTop: '1.5rem', fontWeight: 'bold' }}>
            ⚠️ IMPORTANT: You MUST click one of these two buttons for every code snippet to proceed to the next one.
          </p>
        </div>

        <div className="instruction-section" style={{ backgroundColor: '#f0f4c3', padding: '1.5rem', borderRadius: '8px', marginTop: '1.5rem' }}>
          <h2>💡 REMEMBER</h2>
          <ul style={{ fontSize: '15px', lineHeight: '1.8' }}>
            <li><strong>This is YOUR work project</strong> - Act like you normally would when reviewing code</li>
            <li><strong>Read each scenario and code snippet carefully</strong> - Imagine you really need this code</li>
            <li><strong>Click ONE of the two buttons</strong> (USE or REJECT) for every code snippet</li>
            <li><strong>Answer the follow-up question</strong> after each decision</li>
            <li><strong>Complete all 4 code reviews</strong> to finish this task</li>
            <li><strong>There are no wrong answers</strong> - we want YOUR real developer judgment</li>
            <li><strong>Take your time</strong> - this is not a speed test</li>
          </ul>
        </div>

        <div className="instruction-section" style={{ marginTop: '1.5rem', textAlign: 'center', padding: '1rem', backgroundColor: '#e8f5e9', borderRadius: '8px' }}>
          <p style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '0.5rem' }}>
            ✅ Ready to start? Click the button below when you're ready to see the first code snippet.
          </p>
          <p style={{ fontSize: '14px', color: '#555' }}>
            Time estimate: 10-15 minutes
          </p>
        </div>

        <div className="button-container">
          <button onClick={handleContinue} className="btn btn-primary">
            Begin Code Review Task
          </button>
        </div>
      </div>
    </div>
  );
}

export default CodingTaskIntro;
